import "./TradeHistoryComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, IScope} from "angular";
import {AccountPerformanceViewStore} from "../../service/AccountPerformanceViewStore";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {TradeType} from "../../models/TradeType";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {ascending, distinct} from "../../../js/util/UtilFunctions";
import {HistoricalTrades} from "../../models/HistoricalTradeData";

export const TradeHistory = "tfTradeHistory";

export function tradeHistory(): IComponentOptions {
    return {
        templateUrl: "app/performance/components/trade-history/TradeHistoryComponent.html",
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: TradeHistoryComponentController
    };
}

export class TradeHistoryComponentController implements IOnInit, IOnDestroy {

    public readonly viewStore: AccountPerformanceViewStore;

    public readonly trades = new ReplaySubject<HistoricalTrades>(1);
    public readonly underlyings$ = new ReplaySubject<ReadonlyArray<string>>(1);
    public exportDataUrl$: Observable<string>;

    public readonly startDate = new ReplaySubject<IsoDate>(1);
    public readonly endDate = new ReplaySubject<IsoDate>(1);

    public underlyingFilter: string | null = "";
    public includeEquities = true;
    public includeOptions = true;

    private readonly textFilter$ = new BehaviorSubject<string | null>("");
    private readonly includeEquities$ = new BehaviorSubject(true);
    private readonly includeOptions$ = new BehaviorSubject(true);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(public $scope: IScope, public currentUserService: CurrentUserService) {
    }

    public $onInit(): void {
        this.exportDataUrl$ = this.viewStore
                                  .exportDataUrl
                                  .takeUntil(this.destroyed$);
        this.watchUnderlyings();
        this.initTrades();
        this.initDateRange();
    }

    private watchUnderlyings() {
        this.viewStore
            .trades
            .takeUntil(this.destroyed$)
            .map((trades) => this.underlyingsForTrades(trades))
            .subscribe((underlyings) => this.underlyings$.next(underlyings));
    }

    private underlyingsForTrades(trades: HistoricalTrades): ReadonlyArray<string> {
        return trades.map((tr) => tr.underlying)
                     .filter(distinct)
                     .sort(ascending);
    }

    private initTrades() {
        const text$ = this.textFilter$.observeOn(Scheduler.async);
        const equities$ = this.includeEquities$.observeOn(Scheduler.async);
        const options$ = this.includeOptions$.observeOn(Scheduler.async);

        Observable.combineLatest(this.viewStore.trades, text$, equities$, options$)
                  .debounceTime(200)
                  .takeUntil(this.destroyed$)
                  .map(([trades, textFilter, includeEquities, includeOptions]) => this.filterTrades(trades,
                                                                                                    textFilter,
                                                                                                    includeEquities,
                                                                                                    includeOptions))
                  .subscribe((trades) => this.trades.next(trades));
    }

    private initDateRange() {
        this.viewStore
            .dateRange
            .takeUntil(this.destroyed$)
            .subscribe(([from, to]) => {
                this.startDate.next(from);
                this.endDate.next(to);
            });
    }

    public updateTextFilter(filter: string) {
        this.textFilter$.next(filter);
    }

    public updateIncludeEquities(include: boolean) {
        this.includeEquities$.next(include);
    }

    public updateIncludeOptions(include: boolean) {
        this.includeOptions$.next(include);
    }

    private filterTrades(trades: HistoricalTrades,
                         textFilter: string | null,
                         includeEquities: boolean,
                         includeOptions: boolean): HistoricalTrades {

        const filteredTrades = [...trades];
        return filteredTrades.filter((t) => t.tradeType === TradeType.EQUITY ? includeEquities : true)
                             .filter((t) => t.tradeType === TradeType.OPTION ? includeOptions : true)
                             .filter((t) => textFilter == null || textFilter.length === 0 || t.code.startsWith(textFilter.toUpperCase()));
    }

    public $onDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
